import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { FormError } from '@/components/FormError'
import { QrCode } from '@/components/QrCode'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import {
  bothKnow,
  connectUrl,
  useIncoming,
  useMakeLink,
  useRefreshConnections,
  type Account,
  type ConnectionRequest,
} from '@/lib/connections'
import { Together } from './Together'

// Your connect link, as a QR code to scan or a link to send. A fresh one each time this opens, working for 7 days;
// whoever opens it can ask to connect, and you still say yes. Its secret is only here.
// While it's open, it checks every few seconds for anyone asking from a link, so someone who scans it in front of you
// shows up right here to accept or not; once you accept, you both see that you're connected.
export function MyLink({ open, onClose, name }: { open: boolean; onClose: () => void; name: string }) {
  const make = useMakeLink()
  const refresh = useRefreshConnections()
  const incoming = useIncoming(open)
  // Requests already waiting when this opened are on People; only ones that come in now show here.
  const [before, setBefore] = useState<string[] | null>(null)
  const [setAside, setSetAside] = useState<string[]>([])
  const [joined, setJoined] = useState<Account | null>(null)
  if (open && before === null && incoming.data) setBefore(incoming.data.requests.map((x) => x.id))
  const arrived =
    before === null
      ? undefined
      : incoming.data?.requests.find((x) => x.link && !before.includes(x.id) && !setAside.includes(x.id))
  const asked = useRef(false)
  const [show, setShow] = useState<'qr' | 'link'>('qr')
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    if (open && !asked.current) {
      asked.current = true
      make.mutate()
    }
    if (!open) asked.current = false
    // make is stable enough: only opening asks for a new link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  const link = make.data ? connectUrl(make.data.token) : ''
  const canShare = typeof navigator.share === 'function'

  async function copy() {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
    } catch {
      // Some browsers refuse; the link is on screen to copy by hand.
    }
  }
  async function share() {
    try {
      await navigator.share({ title: `Connect with ${name}`, text: `Connect with ${name} on gatheredfrom`, url: link })
    } catch {
      // Closing the share sheet without picking anything is fine.
    }
  }

  function close() {
    onClose()
    setCopied(false)
    setShow('qr')
    setBefore(null)
    setSetAside([])
    setJoined(null)
    void refresh()
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-md">
        {joined ? (
          <>
            <DialogHeader className="sr-only">
              <DialogTitle>Connected</DialogTitle>
              <DialogDescription>You’re connected now.</DialogDescription>
            </DialogHeader>
            <Together other={joined} onDone={close} />
          </>
        ) : arrived ? (
          <Arrived
            request={arrived}
            onAccepted={() => setJoined(arrived.from)}
            onNotNow={() => setSetAside((x) => [...x, arrived.id])}
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Your connect link</DialogTitle>
              <DialogDescription>
                Anyone who opens it can ask to connect with you, and you still choose whether to accept. It works for 7
                days.
              </DialogDescription>
            </DialogHeader>
            <FormError error={make.error} />
            {make.data &&
              (show === 'qr' ? (
                <div className="grid justify-items-center gap-3 text-center">
                  <QrCode text={link} label="QR code to connect with you" className="w-64 max-w-full rounded-xl" />
                  <p className="text-sub text-balance text-muted-foreground">
                    Have them tap + on People, then Scan a QR code. Their request shows up here.
                  </p>
                </div>
              ) : (
                <div className="grid gap-2">
                  <Label htmlFor="connect-link">Your link</Label>
                  <Input id="connect-link" readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
                  <div className="flex flex-wrap gap-2">
                    {canShare && (
                      <Button type="button" onClick={share}>
                        Send the link
                      </Button>
                    )}
                    <Button type="button" variant={canShare ? 'outline' : 'default'} onClick={copy}>
                      {copied ? 'Copied' : 'Copy the link'}
                    </Button>
                  </div>
                </div>
              ))}
            {make.data && (
              <Button variant="outline" className="self-start" onClick={() => setShow(show === 'qr' ? 'link' : 'qr')}>
                {show === 'qr' ? 'Send as a link instead' : 'Show as a QR code'}
              </Button>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

// Someone who just scanned your code, asking: who they are and who you both know, and your answer.
function Arrived({
  request,
  onAccepted,
  onNotNow,
}: {
  request: ConnectionRequest
  onAccepted: () => void
  onNotNow: () => void
}) {
  const answer = useMutation({
    mutationFn: (yes: boolean) =>
      api(`/me/connections/requests/${request.id}/${yes ? 'accept' : 'decline'}`, { method: 'POST' }),
    onSuccess: (_, yes) => (yes ? onAccepted() : onNotNow()),
  })
  const first = request.from.name.trim().split(/\s+/)[0] || request.from.name
  return (
    <div className="arrived flex flex-col items-center gap-5 text-center">
      <Avatar person={request.from} variant="display" className="size-28 text-5xl" />
      <DialogHeader className="items-center text-center sm:text-center">
        <DialogTitle className="text-title text-balance">{request.from.name} wants to connect</DialogTitle>
        <DialogDescription className="text-balance">
          {request.mutual.length ? `You both know ${bothKnow(request.mutual)}.` : 'They just scanned your code.'}
        </DialogDescription>
      </DialogHeader>
      {request.note && <blockquote className="text-body text-balance italic">“{request.note}”</blockquote>}
      <FormError error={answer.error} />
      <div className="flex w-full flex-col gap-2">
        <Button disabled={answer.isPending} onClick={() => answer.mutate(true)}>
          Accept
        </Button>
        <Button variant="ghost" disabled={answer.isPending} onClick={() => answer.mutate(false)}>
          Not now
        </Button>
        <p className="text-xs text-muted-foreground">{first} isn’t told if you choose Not now.</p>
      </div>
    </div>
  )
}
